import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, BookOpen, Briefcase, Award, TrendingUp, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import { useLanguage } from "@/contexts/LanguageContext";

interface Stats {
  totalProjects: number;
  totalCourses: number;
  totalStudents: number;
  totalEnrollments: number;
}

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState<Stats>({
    totalProjects: 0,
    totalCourses: 0,
    totalStudents: 0,
    totalEnrollments: 0,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
      if (!data) {
        toast({
          title: "Access Denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        navigate("/");
      }
    };

    checkAdminRole();
  }, [user, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  const fetchStats = async () => {
    try {
      const [projectsData, coursesData, studentsData, enrollmentsData] = await Promise.all([
        supabase.from("portfolio_projects").select("id", { count: "exact", head: true }),
        supabase.from("courses").select("id", { count: "exact", head: true }),
        supabase.from("user_roles").select("id", { count: "exact", head: true }).eq("role", "student"),
        supabase.from("enrollments").select("id", { count: "exact", head: true }),
      ]);

      setStats({
        totalProjects: projectsData.count || 0,
        totalCourses: coursesData.count || 0,
        totalStudents: studentsData.count || 0,
        totalEnrollments: enrollmentsData.count || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const adminSections = [
    {
      title: t("admin.portfolioMgmt"),
      description: t("admin.portfolioMgmtDesc"),
      icon: Briefcase,
      route: "/admin/portfolio",
      color: "from-primary to-primary/80",
    },
    {
      title: t("admin.courseMgmt"),
      description: t("admin.courseMgmtDesc"),
      icon: BookOpen,
      route: "/admin/courses",
      color: "from-secondary to-secondary/80",
    },
    {
      title: t("admin.userMgmt"),
      description: t("admin.userMgmtDesc"),
      icon: Users,
      route: "/admin/users",
      color: "from-accent to-accent/80",
    },
    {
      title: t("admin.settings"),
      description: t("admin.settingsDesc"),
      icon: Settings,
      route: "/admin/settings",
      color: "from-muted-foreground to-muted-foreground/80",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-6">
      <div className="container mx-auto max-w-7xl">
        <button
          onClick={() => navigate("/")}
          className="mb-6 group flex items-center gap-2 text-foreground/60 hover:text-foreground transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>{t("admin.backToHome")}</span>
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            {t("admin.title")}
          </h1>
          <p className="text-muted-foreground">{t("admin.welcome")}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("admin.totalProjects")}
              </CardTitle>
              <Briefcase className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalProjects}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.portfolioItems")}</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-secondary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("admin.totalCourses")}
              </CardTitle>
              <BookOpen className="w-4 h-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalCourses}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.publishedDrafts")}</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-accent/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("admin.totalStudents")}
              </CardTitle>
              <Users className="w-4 h-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalStudents}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.registeredUsers")}</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("admin.enrollments")}
              </CardTitle>
              <Award className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalEnrollments}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.activeCompleted")}</p>
            </CardContent>
          </Card>
        </div>

        {/* Admin Sections */}
        <div>
          <h2 className="text-2xl font-bold mb-4">{t("admin.quickActions")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {adminSections.map((section) => {
              const Icon = section.icon;
              return (
                <Card
                  key={section.route}
                  className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden relative"
                  onClick={() => navigate(section.route)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${section.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 flex items-center gap-2">
                          <Icon className="w-5 h-5" />
                          {section.title}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{section.description}</p>
                      </div>
                      <TrendingUp className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      {t("admin.manage")}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
